// @givmo/board — governance-parameter decode + display helpers.
//
// ══ THE ONE RULE THIS MODULE EXISTS TO HOLD ═════════════════════════════════════════════════════
//
// A governance parameter is a number A HUMAN AT THE ORGANIZATION ENTERED, with a citation to the section of
// the organization's own governing instrument that sets it. The product never derives one, never reads it
// out of an uploaded document, and never states what an instrument requires (plan §3.2 Band 3). So:
//
//   * `value_summary` is ECHOED VERBATIM. The backend renders a fraction as the organization wrote it
//     ("2 of 3") rather than collapsing it to a percentage, precisely so a recorded value cannot
//     quietly become a derived one. This module never re-renders it from `numerator`/`denominator`.
//   * `bylaw_citation` is NOT NULL and non-blank at the database and required on the command, so
//     there is no path by which a number arrives un-attributed. It is displayed with every value.
//   * Versioning is SUPERSEDE, never edit. A replacement points the incumbent at itself; the history
//     is the chain. Rendering only the active row would hide that a figure used to be different.

import { extractList } from '@givmo/api-client/list';
import { humanize } from '../../lib/display';
export { displayDate } from '../../lib/display';
import type { OrgBoardGovernanceParameterOut } from '@givmo/api-client';
import { minutesRuleWords as rc11 } from '../minutes/minutes-rule-words';
import { isTermRuleKind } from '../roster/terms-decode';

/**
 * CBG-RC11 (D-CBG-26): the board's two RULES for approving minutes, recorded as a way (`basis`
 * `choice`, `choice_value`) rather than a number. Each has its own card on Meeting rules, always shown
 * — with Givmo's starting point said as that until the board records its own — so the number cards'
 * grouping leaves them out.
 */
export const APPROVAL_RULE_KINDS = ['minutes_approval', 'closed_session_minutes_approval'] as const;
export type ApprovalRuleKind = (typeof APPROVAL_RULE_KINDS)[number];

/** Each rule's way with nothing recorded: Givmo's starting point (backend `board_minutes.md` §A2). */
export const APPROVAL_RULE_DEFAULT: Record<ApprovalRuleKind, string> = {
  minutes_approval: 'written_consent',
  closed_session_minutes_approval: 'same_way',
};

/**
 * The ways each rule records, in the order the dialog offers them (§A2): CBG-RC10 adds the closed
 * rule's `directors_present` between the two RC11 offered (backend §S2, the derivation's order).
 */
export const APPROVAL_RULE_WAYS = {
  minutes_approval: ['written_consent', 'next_meeting', 'officer'],
  closed_session_minutes_approval: ['same_way', 'directors_present', 'later_meeting'],
} as const;

export function isApprovalRuleKind(kind: string | null | undefined): kind is ApprovalRuleKind {
  return (APPROVAL_RULE_KINDS as readonly string[]).includes((kind ?? '').toLowerCase());
}

/** One approval rule as its card shows it: the row in force (if any) and its earlier versions. */
export interface ApprovalRule {
  kind: ApprovalRuleKind;
  active: OrgBoardGovernanceParameterOut | null;
  superseded: OrgBoardGovernanceParameterOut[];
  /** The way in force: the recorded one, else Givmo's starting point. */
  way: string;
}

export function approvalRule(
  parameters: readonly OrgBoardGovernanceParameterOut[],
  kind: ApprovalRuleKind,
): ApprovalRule {
  const rows = parameters.filter((row) => (row.parameter_kind ?? '').toLowerCase() === kind);
  const active = rows.find(isActive) ?? null;
  const superseded = rows
    .filter((row) => !isActive(row))
    .sort((left, right) => instant(right.recorded_at) - instant(left.recorded_at));
  const recorded = (active?.choice_value ?? '').toLowerCase();
  return {
    kind,
    active,
    superseded,
    way: recorded.length > 0 ? recorded : APPROVAL_RULE_DEFAULT[kind],
  };
}

/**
 * Parameters from `GET /board/{org_ref}/governance-parameters`. That route is
 * TYPED as a bare array (not the paginated envelope), but `extractList` reads the bare array, an
 * un-unwrapped `{data:[…]}` envelope, or `[]` — never throwing on an unexpected shape.
 */
export function extractParameters(body: unknown): OrgBoardGovernanceParameterOut[] {
  return extractList<OrgBoardGovernanceParameterOut>(body, 'data');
}

/** Whether a parameter row is the one currently in force. */
export function isActive(parameter: OrgBoardGovernanceParameterOut): boolean {
  return (parameter.status ?? '').toUpperCase() === 'ACTIVE';
}

/**
 * The label for a parameter's KIND, including which meetings it applies to.
 *
 * A notice period is recorded per meeting type; quorum and voting thresholds are board-wide today. A
 * missing meeting type on a notice period is not an error state — it just means the organization has not
 * recorded one for that kind of meeting yet.
 */
export function parameterLabel(parameter: {
  parameter_kind?: string | null;
  meeting_type?: string | null;
}): string {
  const kind = (parameter.parameter_kind ?? '').toLowerCase();
  const meetingType = parameter.meeting_type ?? null;
  if (kind === 'notice_period') {
    return meetingType !== null
      ? `Notice period · ${meetingTypeLabel(meetingType)} meetings`
      : 'Notice period';
  }
  if (kind === 'quorum') return 'Quorum';
  if (kind === 'voting_threshold') return 'Voting threshold';
  if (kind === 'minutes_approval') return rc11.minutesRuleTitle;
  if (kind === 'closed_session_minutes_approval') return rc11.closedRuleTitle;
  return humanize(kind);
}

/** Human label for a meeting type. */
export function meetingTypeLabel(meetingType: string | null | undefined): string {
  switch ((meetingType ?? '').toLowerCase()) {
    case 'regular':
      return 'Regular';
    case 'annual':
      return 'Annual';
    case 'special':
      return 'Special';
    default:
      return humanize(meetingType);
  }
}

/**
 * Group the rows by the key an organization thinks in — the kind, plus the meeting type where that kind has
 * one. Each group's ACTIVE row leads and the superseded rows follow, newest first, so the history of a
 * single figure reads as one story.
 */
export interface ParameterGroup {
  key: string;
  label: string;
  active: OrgBoardGovernanceParameterOut | null;
  superseded: OrgBoardGovernanceParameterOut[];
}

export function groupParameters(
  parameters: readonly OrgBoardGovernanceParameterOut[],
): ParameterGroup[] {
  const groups = new Map<string, ParameterGroup>();

  for (const parameter of parameters) {
    // The approval rules have their own cards (`approvalRule`), and the three term rules live on the
    // Terms tab (CBG-YR3-TERMS).
    if (isApprovalRuleKind(parameter.parameter_kind)) continue;
    if (isTermRuleKind(parameter.parameter_kind)) continue;
    const key = `${parameter.parameter_kind ?? ''}:${parameter.meeting_type ?? ''}`;
    const existing = groups.get(key) ?? {
      key,
      label: parameterLabel(parameter),
      active: null,
      superseded: [],
    };
    if (isActive(parameter)) {
      existing.active = parameter;
    } else {
      existing.superseded.push(parameter);
    }
    groups.set(key, existing);
  }

  for (const group of groups.values()) {
    group.superseded.sort((left, right) => instant(right.recorded_at) - instant(left.recorded_at));
  }

  return [...groups.values()].sort((left, right) => left.label.localeCompare(right.label));
}

function instant(iso: string | null | undefined): number {
  if (!iso) return 0;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? 0 : value;
}
